import { OnInit, AfterViewInit, OnDestroy, Component, Input, ViewChild, TemplateRef, Injector } from '@angular/core';
import { Subscription, Observable } from 'rxjs';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FilterService } from '../../../../../Common/Filter/services/abstract-filter.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';

import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { EntityTypeDefaultImageEnum } from '../../../../../Common/Misc/enums/entity-type-default-image.enum';
import { TableColumnInputEnum } from '../../../../../Common/Forms/enums/table-column-input-type.enum';
import { TableColumnModel } from '../../../../../Common/Forms/models/table-column.model';
import { RecipientResultModel } from '../../../models/recipient.result.model';
import { topStatLabels } from '../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-map.model';

import { NegativeRecipientSearchService } from './negative-recipient-search.service';
import { RemoveFromListsConfirmDialogComponent } from '../RemoveFromListsConfirmDialog/remove-from-lists-confirm-dialog.component';
import { TargetingRhpContextsService } from '../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { ListCheckbox } from '../RemoveFromListsConfirmDialog/remove-from-lists-confirm-dialog.component';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';

@Component({
    selector: 'agpr-remove-from-lists-dialog',
    templateUrl: './remove-from-lists-dialog.component.html',
    styleUrls: ['./remove-from-lists-dialog.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: NegativeRecipientSearchService
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: NegativeRecipientSearchService
        }
    ],
    standalone: false
})

export class RemoveFromListsDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    readonly id: string = 'recipientReport_removeRecipientFromListsDialog';
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly entityType = EntityType;
    readonly entityTypeDefaultImageEnum = EntityTypeDefaultImageEnum;
    readonly topStatLabels = topStatLabels;

    private subscriptions: Subscription[] = [];

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;
    isRemoved: boolean = false;

    totalCount: number = 0; // total number of recipients (of all pages)
    recipients: RecipientResultModel[]; // recipient list of the current page
    highlightedRow: RecipientResultModel; // the active recipient/outlet shown on the RHP
    selectedRecipients: Map<string, RecipientResultModel> = new Map(); // the checked/selected recipients of the current page
    isAllRecipientsSelected: boolean = false; // whether all recipients (of all pages) are selected

    columnDefs: TableColumnModel[] = [];
    @ViewChild('recipientsCell') public recipientsCellTemplate: TemplateRef<any>;
    @ViewChild('issueCell') public issueCellTemplate: TemplateRef<any>;
    @ViewChild('listsCell') public listsCellTemplate: TemplateRef<any>;

    constructor(
        public coreDialogService: CoreDialogService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public negativeRecipientSearchService: NegativeRecipientSearchService,
        public injector: Injector
    ) {
        super(coreDialogService, 'RemoveFromLists');
        this.negativeRecipientSearchService.resetSearch();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.negativeRecipientSearchService.getResults().subscribe(recipients => {
                if(recipients){
                    this.recipients = recipients.map(recipient => {
                        (recipient as any).editDisabled = !(recipient?.listMembershipChips?.length > 0);
                        return recipient;
                    });
                    this.handleHighlightedRowChanged(recipients[0]);
                    this.selectedRecipients.clear();
                    this.toggleCheckAll(this.isAllRecipientsSelected);
                }
            }),
            this.negativeRecipientSearchService.getItemCount().subscribe(count => {
                this.totalCount = count;
            }),
        );
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.columnDefs = [
                {
                    type: TableColumnInputEnum.checkbox,
                    field: 'checked',
                    width: 46
                },
                {
                    type: TableColumnInputEnum.custom,
                    heading: 'Discover.distributionReport.recipient.removeFromListsDialog.table.recipients',
                    customTemplate: this.recipientsCellTemplate,
                    width: 270
                },
                {
                    type: TableColumnInputEnum.custom,
                    heading: 'Discover.distributionReport.recipient.removeFromListsDialog.table.issue',
                    customTemplate: this.issueCellTemplate,
                    width: 210
                },
                {
                    type: TableColumnInputEnum.custom,
                    heading: 'Discover.distributionReport.recipient.removeFromListsDialog.table.lists',
                    customTemplate: this.listsCellTemplate,
                }
            ];
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    // set RHP to show the information of the hightlighted recipient
    setContactId(id: string) {
        this.targetingRhpContextsService.setContact(id);
    }

    // set RHP to show the information of the hightlighted outlet
    setOutletId(id: string) {
        this.targetingRhpContextsService.setOutlet(id);
    }

    // handle the hightlight for a single recipient/outlet
    // the information of the hightlighted recipient/outlet is shown on the RHP
    handleHighlightedRowChanged(row: RecipientResultModel) {
        if(row.entityType === EntityType.CONTACT) {
            this.setContactId(row.id);
        }
        if(row.entityType === EntityType.OUTLET) {
            this.setOutletId(row.id);
        }
        this.highlightedRow = row;
    }

    // toggle the check for all recipients of the current page
    toggleCheckAll(checked: boolean): void {
        if(!checked) {
            this.isAllRecipientsSelected = false;
        }
        this.recipients.forEach(recipient => {
            (recipient as any).checked = (recipient as any).editDisabled ? false : checked;
            this.updateSelectedRecipient(recipient);
        });
    }

    // toggle the check for all recipients of all pages
    toggleSelectAll(selected: boolean): void {
        this.isAllRecipientsSelected = selected;
        if(!selected){
            this.toggleCheckAll(selected);
        }
    }

    // handle the check for a single recipient
    handleTableItemChanged(row: RecipientResultModel): void {
        if(!(row as any).checked) {
            this.isAllRecipientsSelected = false;
        }
        this.updateSelectedRecipient(row);
    }

    updateSelectedRecipient(recipient: RecipientResultModel){
        const {id, checked} = recipient as any;
        if(checked) {
            this.selectedRecipients.set(id, recipient);
        } else {
            this.selectedRecipients.delete(id);
        }
    }

    removeFromLists(): void {
        if(this.isAllRecipientsSelected) {
            const checkboxes = this.negativeRecipientSearchService.getAllRecipientLists()?.map(
                list => ({
                    id: list.id,
                    label: list.name,
                    checked: true
                })
            );
            this.openConfirmDiaglog(checkboxes, true, this.totalCount);
        } else {
            const checkboxes: ListCheckbox[] = [];
            this.selectedRecipients.forEach(recipient => {
                recipient?.listMemberships?.forEach(list => {
                    const checkbox = checkboxes.find(itm => itm.id === list.id);
                    if(checkbox) {
                        checkbox.recipientIds.push(recipient.id);
                    } else {
                        checkboxes.push({
                            id: list.id,
                            label: list.name,
                            checked: true,
                            recipientIds: [recipient.id],
                            entityType: recipient.entityType
                        });
                    }
                });
            });
            this.openConfirmDiaglog(checkboxes, false, this.selectedRecipients.size);
        }
    }

    openConfirmDiaglog(lists: ListCheckbox[], isAllRecipientsSelected, totalCount: number): void {
        this.coreDialogService.open(RemoveFromListsConfirmDialogComponent, {
            inputs: {
                lists,
                isAllRecipientsSelected,
                totalCount
            },
            outputs: {
                dialogClose: (isRemoved: boolean) => {
                    this.isRemoved = isRemoved;
                    if(isRemoved){
                        this.hideSlideOverlay = true;
                    }
                }
            }

        }, this.injector);
    }

    cancel(): void {
        this.isRemoved = false;
        this.hideSlideOverlay = true;
    }

    handleVisibleChange(): void {
        this.closeDialog(this.isRemoved);
    }
}
